// ============================================
// TAB DE BACKUP PARA UNIFIED STORAGE MODAL
// ============================================

import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Upload,
  Progress,
  Alert,
  Space,
  Tag,
  List,
  Switch,
  DatePicker,
  InputNumber,
  Select,
  Modal,
  message,
  Tooltip,
  Row,
  Col,
  Statistic,
  Typography,
  Divider,
  Form,
  Checkbox,
  Table,
  Empty,
  Popconfirm,
} from 'antd';
import {
  DownloadOutlined,
  UploadOutlined,
  HistoryOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  CloudDownloadOutlined,
  CloudUploadOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  FileZipOutlined,
  SafetyCertificateOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import type { RcFile } from 'antd/es/upload';
import type { ColumnsType } from 'antd/es/table';

import { 
  backupManager, 
  ExportOptions, 
  ImportResult, 
  BackupStats 
} from './export-import-backup';

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

// ============================================
// INTERFACES
// ============================================

interface BackupHistoryItem {
  filename: string;
  timestamp: number;
  size: number;
  metadata: any;
}

interface ImportHistoryItem {
  filename: string;
  timestamp: number;
  result: ImportResult;
  originalMetadata: any;
}

// ============================================
// COMPONENTE PRINCIPAL DEL TAB DE BACKUP
// ============================================

export const BackupTab: React.FC = () => {
  // Estados principales
  const [exportOptions, setExportOptions] = useState<ExportOptions>({
    includePhotos: true,
    includeWorkflows: true,
    includeSyncQueue: true,
    includeMetadata: true,
    compression: 'medium',
    maxFileSize: 100,
  });

  const [exportProgress, setExportProgress] = useState(0);
  const [exportStatus, setExportStatus] = useState<string>('');
  const [exportLoading, setExportLoading] = useState(false);

  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState<string>('');
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  const [backupStats, setBackupStats] = useState<BackupStats | null>(null);
  const [backupHistory, setBackupHistory] = useState<BackupHistoryItem[]>([]);
  const [importHistory, setImportHistory] = useState<ImportHistoryItem[]>([]);

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [historyVisible, setHistoryVisible] = useState(false);
  const [importResultVisible, setImportResultVisible] = useState(false);

  // ============================================
  // EFECTOS Y CARGA DE DATOS
  // ============================================

  useEffect(() => {
    loadBackupData();
  }, []);

  const loadBackupData = async () => {
    try {
      const stats = await backupManager.getBackupStats();
      const history = backupManager.getBackupHistory();
      const imports = backupManager.getImportHistory();

      setBackupStats(stats);
      setBackupHistory(history);
      setImportHistory(imports);
    } catch (error) {
      console.error('Error cargando datos de backup:', error);
      message.error('Error cargando información de backup');
    }
  };

  // ============================================
  // FUNCIONES DE EXPORT
  // ============================================

  const handleExport = async () => {
    setExportLoading(true);
    setExportProgress(0);
    setExportStatus('');

    try {
      await backupManager.exportData(
        exportOptions,
        (progress, status) => {
          setExportProgress(progress);
          setExportStatus(status);
        }
      );

      // Recargar estadísticas después del export
      await loadBackupData();
    } catch (error) {
      console.error('Error durante export:', error);
    } finally {
      setExportLoading(false);
      setExportProgress(0);
      setExportStatus('');
    }
  };

  // ============================================
  // FUNCIONES DE IMPORT
  // ============================================

  const handleImport = async (file: RcFile) => {
    setImportLoading(true);
    setImportProgress(0);
    setImportStatus('');
    setImportResult(null);

    try {
      const result = await backupManager.importData(
        file,
        {
          overwriteExisting: false,
          validateIntegrity: true,
          skipLargeFiles: true,
        },
        (progress, status) => {
          setImportProgress(progress);
          setImportStatus(status);
        }
      );

      setImportResult(result);
      setImportResultVisible(true);

      // Recargar estadísticas después del import
      await loadBackupData();
    } catch (error) {
      console.error('Error durante import:', error);
    } finally {
      setImportLoading(false);
      setImportProgress(0);
      setImportStatus('');
    }

    return false; // Prevenir upload automático
  };

  // ============================================
  // FUNCIONES DE UTILIDAD
  // ============================================

  const updateExportOption = <K extends keyof ExportOptions>(
    key: K,
    value: ExportOptions[K]
  ) => {
    setExportOptions(prev => ({ ...prev, [key]: value }));
  };

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      updateExportOption('dateRange', {
        from: dates[0].toDate(),
        to: dates[1].toDate(),
      });
    } else {
      const newOptions = { ...exportOptions };
      delete newOptions.dateRange;
      setExportOptions(newOptions);
    }
  };

  const clearHistory = () => {
    backupManager.clearBackupHistory();
    setBackupHistory([]);
    setImportHistory([]);
    setBackupStats(null);
    message.success('Historial limpiado');
  };

  // ============================================
  // COLUMNAS PARA TABLAS DE HISTORIAL
  // ============================================

  const backupColumns: ColumnsType<BackupHistoryItem> = [
    {
      title: 'Archivo',
      dataIndex: 'filename',
      key: 'filename',
      ellipsis: true,
      render: (filename: string) => (
        <Tooltip title={filename}>
          <Text strong>{filename}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Fecha',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 150,
      render: (timestamp: number) => (
        <Text>{dayjs(timestamp).format('DD/MM/YYYY HH:mm')}</Text>
      ),
    },
    {
      title: 'Tamaño',
      dataIndex: 'size',
      key: 'size',
      width: 100,
      render: (size: number) => (
        <Text>{backupManager.formatBytes(size)}</Text>
      ),
    },
    {
      title: 'Elementos',
      key: 'elements',
      width: 120,
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <Tag color="green">Fotos: {record.metadata?.categories?.photos || 0}</Tag>
          <Tag color="orange">Workflows: {record.metadata?.categories?.workflows || 0}</Tag>
        </Space>
      ),
    },
  ];

  const importColumns: ColumnsType<ImportHistoryItem> = [
    {
      title: 'Archivo',
      dataIndex: 'filename',
      key: 'filename',
      ellipsis: true,
      render: (filename: string) => (
        <Text strong>{filename}</Text>
      ),
    },
    {
      title: 'Fecha',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 150,
      render: (timestamp: number) => (
        <Text>{dayjs(timestamp).format('DD/MM/YYYY HH:mm')}</Text>
      ),
    },
    {
      title: 'Estado',
      key: 'status',
      width: 100,
      render: (_, record) => (
        <Tag color={record.result.success ? 'green' : 'red'}>
          {record.result.success ? 'Exitoso' : 'Fallido'}
        </Tag>
      ),
    },
    {
      title: 'Importados',
      key: 'imported',
      width: 120,
      render: (_, record) => {
        const total = Object.values(record.result.importedItems).reduce((sum, count) => sum + count, 0);
        return <Tag color="blue">{total} elementos</Tag>;
      },
    },
  ];

  // ============================================
  // RENDERIZADO
  // ============================================

  return (
    <div style={{ padding: '16px 0' }}>
      {/* Estadísticas generales */}
      {backupStats && (
        <Card title="Estadísticas de Backup" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Statistic
                title="Total Backups"
                value={backupStats.totalBackups}
                prefix={<FileZipOutlined />}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Último Backup"
                value={
                  backupStats.lastBackup
                    ? dayjs(backupStats.lastBackup).format('DD/MM/YYYY')
                    : 'Nunca'
                }
                prefix={<CalendarOutlined />}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Espacio Usado"
                value={backupManager.formatBytes(backupStats.totalSize)}
                prefix={<CloudDownloadOutlined />}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Espacio Disponible"
                value={
                  backupStats.availableSpace
                    ? backupManager.formatBytes(backupStats.availableSpace)
                    : 'Desconocido'
                }
                prefix={<SafetyCertificateOutlined />}
              />
            </Col>
          </Row>

          {backupStats.recommendations.length > 0 && (
            <>
              <Divider />
              <Alert
                message="Recomendaciones"
                description={
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {backupStats.recommendations.map((rec, index) => (
                      <li key={index}>{rec}</li>
                    ))}
                  </ul>
                }
                type="info"
                showIcon
              />
            </>
          )}
        </Card>
      )}

      <Row gutter={[8,16]} >
        {/* Panel de Export */}
        <Col span={12} xs={24}>
          <Card
            title={
              <Space>
                <DownloadOutlined />
                Exportar Datos
              </Space>
            }
            extra={
              <Button
                type="link"
                onClick={() => setShowAdvanced(!showAdvanced)}
                icon={<InfoCircleOutlined />}
              >
                {showAdvanced ? 'Ocultar' : 'Avanzado'}
              </Button>
            }
          >
            <Form layout="vertical">
              {/* Opciones básicas */}
              <Form.Item label="Categorías a exportar">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Checkbox
                    checked={exportOptions.includePhotos}
                    onChange={(e) => updateExportOption('includePhotos', e.target.checked)}
                  >
                    Fotos
                  </Checkbox>
                  <Checkbox
                    checked={exportOptions.includeWorkflows}
                    onChange={(e) => updateExportOption('includeWorkflows', e.target.checked)}
                  >
                    Workflows
                  </Checkbox>
                  <Checkbox
                    checked={exportOptions.includeSyncQueue}
                    onChange={(e) => updateExportOption('includeSyncQueue', e.target.checked)}
                  >
                    Cola de Sincronización
                  </Checkbox>
                  <Checkbox
                    checked={exportOptions.includeMetadata}
                    onChange={(e) => updateExportOption('includeMetadata', e.target.checked)}
                  >
                    Metadatos del Sistema
                  </Checkbox>
                </Space>
              </Form.Item>

              {/* Opciones avanzadas */}
              {showAdvanced && (
                <>
                  <Form.Item label="Compresión">
                    <Select
                      value={exportOptions.compression}
                      onChange={(value) => updateExportOption('compression', value)}
                      style={{ width: '100%' }}
                    >
                      <Option value="none">Sin compresión</Option>
                      <Option value="low">Baja</Option>
                      <Option value="medium">Media</Option>
                      <Option value="high">Alta</Option>
                    </Select>
                  </Form.Item>

                  <Form.Item label="Tamaño máximo de archivo (MB)">
                    <InputNumber
                      min={1}
                      max={500}
                      value={exportOptions.maxFileSize}
                      onChange={(value) => updateExportOption('maxFileSize', value || 100)}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>

                  <Form.Item label="Rango de fechas (opcional)">
                    <RangePicker
                      style={{ width: '100%' }}
                      format="DD/MM/YYYY"
                      placeholder={['Fecha inicio', 'Fecha fin']}
                      onChange={handleDateRangeChange}
                    />
                  </Form.Item>
                </>
              )}

              {/* Progreso de export */}
              {exportLoading && (
                <>
                  <Progress percent={exportProgress} status="active" />
                  <Text type="secondary">{exportStatus}</Text>
                </>
              )}

              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleExport}
                loading={exportLoading}
                disabled={
                  !exportOptions.includePhotos &&
                  !exportOptions.includeWorkflows &&
                  !exportOptions.includeSyncQueue
                }
                style={{ width: '100%', marginTop: 16 }}
              >
                Exportar Backup
              </Button>
            </Form>
          </Card>
        </Col>

        {/* Panel de Import */}
        <Col span={12} xs={24}>
          <Card
            title={
              <Space>
                <UploadOutlined />
                Importar Datos
              </Space>
            }
          >
            <Upload.Dragger
              name="backup"
              accept=".zip"
              beforeUpload={handleImport}
              showUploadList={false}
              disabled={importLoading}
              style={{ marginBottom: 16 }}
            >
              <p className="ant-upload-drag-icon">
                <CloudUploadOutlined style={{ fontSize: 48, color: '#1890ff' }} />
              </p>
              <p className="ant-upload-text">
                Arrastra un archivo de backup aquí
              </p>
              <p className="ant-upload-hint">
                Soporta archivos .zip generados por este sistema
              </p>
            </Upload.Dragger>

            {/* Progreso de import */}
            {importLoading && (
              <>
                <Progress percent={importProgress} status="active" />
                <Text type="secondary">{importStatus}</Text>
              </>
            )}

            {/* Advertencias importantes */}
            <Alert
              message="Importante"
              description={
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  <li>Los datos existentes no se sobrescribirán</li>
                  <li>Se validará la integridad de los archivos</li>
                  <li>Los archivos muy grandes se omitirán automáticamente</li>
                </ul>
              }
              type="info"
              showIcon
              style={{ marginTop: 16 }}
            />
          </Card>
        </Col>
      </Row>

      {/* Botón de historial */}
      <div style={{ textAlign: 'center', marginTop: 24 }}>
        <Space>
          <Button
            icon={<HistoryOutlined />}
            onClick={() => setHistoryVisible(true)}
          >
            Ver Historial
          </Button>
          <Popconfirm
            title="¿Limpiar historial?"
            description="Se eliminará todo el historial de backups e imports"
            onConfirm={clearHistory}
            okText="Sí"
            cancelText="No"
          >
            <Button
              icon={<DeleteOutlined />}
              danger
              disabled={backupHistory.length === 0 && importHistory.length === 0}
            >
              Limpiar Historial
            </Button>
          </Popconfirm>
        </Space>
      </div>

      {/* Modal de historial */}
      <Modal
        title="Historial de Backup e Import"
        open={historyVisible}
        onCancel={() => setHistoryVisible(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setHistoryVisible(false)}>
            Cerrar
          </Button>,
        ]}
      >
        <div>
          <Title level={4}>Backups Realizados</Title>
          {backupHistory.length > 0 ? (
            <Table
              columns={backupColumns}
              dataSource={backupHistory.map((item, index) => ({ ...item, key: index }))}
              size="small"
              pagination={{ pageSize: 5 }}
            />
          ) : (
            <Empty description="No hay backups en el historial" />
          )}

          <Divider />

          <Title level={4}>Imports Realizados</Title>
          {importHistory.length > 0 ? (
            <Table
              columns={importColumns}
              dataSource={importHistory.map((item, index) => ({ ...item, key: index }))}
              size="small"
              pagination={{ pageSize: 5 }}
            />
          ) : (
            <Empty description="No hay imports en el historial" />
          )}
        </div>
      </Modal>

      {/* Modal de resultado de import */}
      <Modal
        title="Resultado de Import"
        open={importResultVisible}
        onCancel={() => setImportResultVisible(false)}
        width={600}
        footer={[
          <Button key="close" onClick={() => setImportResultVisible(false)}>
            Cerrar
          </Button>,
        ]}
      >
        {importResult && (
          <div>
            <Alert
              message={importResult.success ? 'Import Exitoso' : 'Import con Errores'}
              type={importResult.success ? 'success' : 'warning'}
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <Card size="small" title="Elementos Importados">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>Fotos: <Tag color="green">{importResult.importedItems.photos}</Tag></div>
                    <div>Workflows: <Tag color="orange">{importResult.importedItems.workflows}</Tag></div>
                    <div>Sync Queue: <Tag color="purple">{importResult.importedItems.syncQueue}</Tag></div>
                  </Space>
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small" title="Elementos Omitidos">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>Duplicados: <Tag color="orange">{importResult.skippedItems.duplicates}</Tag></div>
                    <div>Corruptos: <Tag color="red">{importResult.skippedItems.corrupted}</Tag></div>
                    <div>Muy grandes: <Tag color="gold">{importResult.skippedItems.oversized}</Tag></div>
                  </Space>
                </Card>
              </Col>
            </Row>

            {importResult.warnings.length > 0 && (
              <Alert
                message="Advertencias"
                description={
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {importResult.warnings.map((warning, index) => (
                      <li key={index}>{warning}</li>
                    ))}
                  </ul>
                }
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            {importResult.errors.length > 0 && (
              <Alert
                message="Errores"
                description={
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {importResult.errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                }
                type="error"
                showIcon
              />
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};